import React, { useState } from 'react';
import { X, Mic, Shield, Eye, Database, Clock } from 'lucide-react';

const VoiceConsentModal = ({ isOpen, onAccept, onDecline, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      await onAccept();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    setIsLoading(true);
    try {
      await onDecline();
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <Mic className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Clara Voice Assistant
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Datenschutz & Mikrofon-Berechtigung
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Introduction */}
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Willkommen bei Clara's Voice Assistant! 🎙️
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Clara kann Ihnen mit Sprachbefehlen helfen. Dafür benötigen wir Ihre Zustimmung zur Mikrofon-Nutzung.
            </p>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Mic className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h4 className="font-medium text-gray-900 dark:text-white">Wake Word "Hey Clara"</h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Aktivieren Sie Clara einfach durch "Hey Clara" - keine Klicks nötig.
              </p>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
                <h4 className="font-medium text-gray-900 dark:text-white">Lokale Verarbeitung</h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Ihre Sprache wird lokal in Ihrem Browser verarbeitet.
              </p>
            </div>
          </div>

          {/* Privacy Information */}
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              Datenschutz & Sicherheit
            </h4>
            
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-start gap-2">
                <Eye className="w-4 h-4 mt-0.5 text-blue-500" />
                <div>
                  <strong>Keine Aufzeichnung:</strong> Ihre Sprache wird nicht gespeichert oder übertragen.
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <Database className="w-4 h-4 mt-0.5 text-green-500" />
                <div>
                  <strong>Lokale Verarbeitung:</strong> Spracherkennung erfolgt direkt in Ihrem Browser.
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-purple-500" />
                <div>
                  <strong>Jederzeit widerrufbar:</strong> Sie können die Berechtigung jederzeit in den Einstellungen ändern.
                </div>
              </div>
            </div>
          </div>

          {/* Legal Notice */}
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 p-3 rounded">
            <p className="mb-2">
              <strong>Rechtliche Hinweise (DSGVO Art. 6, 7):</strong>
            </p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Ihre Zustimmung wird mit Zeitstempel und IP-Adresse dokumentiert</li>
              <li>Sie können Ihre Einwilligung jederzeit widerrufen</li>
              <li>Bei Ablehnung können Sie Clara weiterhin per Klick verwenden</li>
              <li>Keine Daten werden an Dritte weitergegeben</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              onClick={handleAccept}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
              Voice Assistant aktivieren
            </button>
            
            <button
              onClick={handleDecline}
              disabled={isLoading}
              className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 px-6 py-3 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Nur per Klick verwenden
            </button>
          </div>

          {/* Additional Info */}
          <div className="text-center text-xs text-gray-500 dark:text-gray-400">
            Sie können diese Einstellung jederzeit in Ihrem Profil unter "Datenschutz-Einstellungen" ändern.
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceConsentModal;

